import { useEffect } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';

import { Chat, EntityName } from 'features/chat';
import { fileLoaderApi, FileLoader, EntityType } from 'features/file-loader';
import { userSpaceRenovationRequestsApi } from '../../api';

import { PageLoader, NoData, Error } from 'components';
import useTranslation from 'shared/hooks/useTranslation';
import CaseHeader from 'shared/components/CaseHeader';
import {
  Card,
  CardContainer,
  CardTitle,
  CardsColumn,
  CardsContainer,
  CaseContainer,
} from 'shared/components/styled/Cases';
import useCooperativeId from 'shared/hooks/useCooperativeId';

import RepairCaseInfo from './RepairCaseInfo';
import { CaseStateCode } from 'utils/enums';

type SpaceRenovationRequestViewFormProps = {
  caseId: string;
};

const SpaceRenovationRequestViewForm = ({
  caseId,
}: SpaceRenovationRequestViewFormProps) => {
  const { t } = useTranslation();

  const cooperativeId = useCooperativeId();

  const [getUserSpaceRenovation, getUserSpaceRenovationQueryState] =
    userSpaceRenovationRequestsApi.useLazyGetUserSpaceRenovationRequestQuery();

  fileLoaderApi.useGetAttachmentsQuery(
    cooperativeId
      ? {
          cooperativeId,
          entityId: caseId,
          entityType: EntityType.Repair,
          cached: true,
        }
      : skipToken,
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const repairCase = getUserSpaceRenovationQueryState.data;

  useEffect(() => {
    if (caseId) {
      getUserSpaceRenovation(caseId);
    }
  }, [caseId]);

  if (getUserSpaceRenovationQueryState.isError) return <Error />;
  if (
    getUserSpaceRenovationQueryState.isLoading ||
    getUserSpaceRenovationQueryState.isFetching
  )
    return <PageLoader />;
  if (!repairCase) return <NoData />;

  return (
    <CaseContainer>
      <CaseHeader
        title="Muutostyöilmoitus"
        number={repairCase.Title}
        status={repairCase.Status}
      />
      <CardsContainer alignItems="stretch">
        <CardsColumn>
          <RepairCaseInfo repairCase={repairCase} />
          <CardContainer>
            <CardTitle>{t('Documents')}</CardTitle>
            <Card>
              <FileLoader
                cached
                entityId={caseId}
                entityType={EntityType.Repair}
              />
            </Card>
          </CardContainer>
        </CardsColumn>
        <CardsColumn>
          <CardContainer flexBasis="60%">
            <CardTitle>{t('Chat')}</CardTitle>
            <Card sx={{ padding: 0, flex: 1 }}>
              <Chat
                entityId={repairCase.Id}
                entityName={EntityName.Incident}
                readonly={repairCase.State?.Value != CaseStateCode.Active}
              />
            </Card>
          </CardContainer>
        </CardsColumn>
      </CardsContainer>
    </CaseContainer>
  );
};

export default SpaceRenovationRequestViewForm;
